import React, { useMemo } from 'react'

import { useQuery } from '@apollo/client'

import { GET_CURRENT_CUSTOMER_ORDERS, GET_CURRENT_CUSTOMER_RESERVATIONS } from 'Const'

import { QueryState } from '../../components/QueryState'
import { ProfileTabKey } from '../../TabNav'
import { OrderCard } from '../Orders/OrderCard'
import { Order } from '../Orders/types'
import { groupOrders } from '../Orders/utils'
import { ReservationCard } from '../Reservations/Card'
import { Reservation } from '../Reservations/types'
import { groupReservations } from '../Reservations/utils'

const RECENT_ORDERS_LIMIT = 2
const UPCOMING_RESERVATIONS_LIMIT = 2

interface HomeTabProps {
  onNavigateTab: (tab: ProfileTabKey) => void
}

export const HomeTab: React.FC<HomeTabProps> = ({ onNavigateTab }) => {
  const ordersResult = useQuery(GET_CURRENT_CUSTOMER_ORDERS)
  const reservationsResult = useQuery(GET_CURRENT_CUSTOMER_RESERVATIONS)

  const orders: Order[] = useMemo(() => ordersResult.data?.currentCustomer?.orders ?? [], [ordersResult.data])
  const reservations: Reservation[] = useMemo(
    () => reservationsResult.data?.currentCustomer?.reservations ?? [],
    [reservationsResult.data],
  )

  const { active: activeOrders, upcoming: upcomingOrders, history } = useMemo(() => groupOrders(orders), [orders])
  const { upcoming: upcomingReservations } = useMemo(() => groupReservations(reservations), [reservations])

  const recentOrders = history.slice(0, RECENT_ORDERS_LIMIT)
  const nextReservations = upcomingReservations.slice(0, UPCOMING_RESERVATIONS_LIMIT)

  const ordersEmpty = !ordersResult.loading && !ordersResult.error
  const reservationsEmpty = !reservationsResult.loading && !reservationsResult.error

  return (
    <div className="user-profile-panel user-profile-panel_enter">
      <section className="user-profile-section">
        <div className="user-profile-section__header">
          <h2 className="user-profile-section__title">Upcoming Reservations</h2>
          <button className="user-profile-link-button" type="button" onClick={() => onNavigateTab('reservations')}>
            View All
          </button>
        </div>
        <QueryState
          emptyLabel="No upcoming reservations"
          error={reservationsResult.error}
          isEmpty={reservationsEmpty && nextReservations.length === 0}
          loading={reservationsResult.loading}
          loadingLabel="Loading reservations…"
        >
          <div className="user-profile-reservation-list">
            {nextReservations.map((reservation) => (
              <ReservationCard key={reservation.id} reservation={reservation} />
            ))}
          </div>
        </QueryState>
      </section>

      <section className="user-profile-section">
        <div className="user-profile-section__header">
          <h2 className="user-profile-section__title">Active Orders</h2>
          <button className="user-profile-link-button" type="button" onClick={() => onNavigateTab('orders')}>
            View All
          </button>
        </div>
        <QueryState
          emptyLabel="No active orders"
          error={ordersResult.error}
          isEmpty={ordersEmpty && activeOrders.length === 0}
          loading={ordersResult.loading}
          loadingLabel="Loading your orders…"
        >
          <div className="user-profile-order-list">
            {activeOrders.map((order) => (
              <OrderCard key={order.uuid ?? order.id} order={order} />
            ))}
          </div>
        </QueryState>
      </section>

      <section className="user-profile-section">
        <div className="user-profile-section__header">
          <h2 className="user-profile-section__title">Upcoming Orders</h2>
          <button className="user-profile-link-button" type="button" onClick={() => onNavigateTab('orders')}>
            View All
          </button>
        </div>
        <QueryState
          emptyLabel="No upcoming orders"
          error={ordersResult.error}
          isEmpty={ordersEmpty && upcomingOrders.length === 0}
          loading={ordersResult.loading}
          loadingLabel="Loading your orders…"
        >
          <div className="user-profile-order-list">
            {upcomingOrders.map((order) => (
              <OrderCard key={order.uuid ?? order.id} order={order} />
            ))}
          </div>
        </QueryState>
      </section>

      <section className="user-profile-section">
        <div className="user-profile-section__header">
          <h2 className="user-profile-section__title">Recent Orders</h2>
          <button className="user-profile-link-button" type="button" onClick={() => onNavigateTab('orders')}>
            View History
          </button>
        </div>
        <QueryState
          emptyLabel="No recent orders"
          error={ordersResult.error}
          isEmpty={ordersEmpty && recentOrders.length === 0}
          loading={ordersResult.loading}
          loadingLabel="Loading your orders…"
        >
          <div className="user-profile-order-list">
            {recentOrders.map((order) => (
              <OrderCard key={order.uuid ?? order.id} order={order} />
            ))}
          </div>
        </QueryState>
      </section>
    </div>
  )
}
